/**
 * useAgreedCheckoutMutation — hora de sortida pactada (portes v1, sprint 06 §4).
 *
 * `PATCH /reservations/:id/agreed-checkout` `{ time: 'HH:mm' | null }` (ADMIN i STAFF). Només escriu
 * a la DB del PMS (Beds24 no en sap res) i el backend recalcula de seguida el codi de les portes.
 * Després es refresquen la fitxa, el calendari (senyals de sortida) i «Posar al dia».
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/services/api';
import { apiErrorMessage } from '@/hooks/useJoanDoors';

export interface AgreedCheckoutVariables {
  reservationId: string;
  /** `"HH:mm"`, o `null` per treure-la (12:00). */
  time: string | null;
}

export interface AgreedCheckoutResponse {
  id: string;
  agreedCheckoutTime: string | null;
}

/** La crida en si, fora del hook perquè es pugui provar amb `api` mockejat. */
export async function patchAgreedCheckout({ reservationId, time }: AgreedCheckoutVariables) {
  const response = await api.patch<AgreedCheckoutResponse>(
    `/reservations/${encodeURIComponent(reservationId)}/agreed-checkout`,
    { time },
  );
  return response.data;
}

export function useAgreedCheckoutMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: patchAgreedCheckout,
    onSuccess: (_data: AgreedCheckoutResponse, { reservationId, time }: AgreedCheckoutVariables) => {
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['stay-catch-up'] });
      queryClient.invalidateQueries({ queryKey: ['reservation-full', reservationId] });
      toast.success(time ? `Sortida pactada a les ${time}` : 'Sense hora de sortida pactada (12:00)');
    },
    onError: (error: unknown) => {
      // El missatge del backend (400 de la franja, 404), no el genèric de la petició.
      toast.error(apiErrorMessage(error, "No s'ha pogut desar l'hora de sortida pactada"));
    },
  });
}

export default useAgreedCheckoutMutation;
